import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Radio, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import Step1Welcome from "@/components/onboarding/Step1Welcome";
import Step2Station from "@/components/onboarding/Step2Station";
import Step3Branding from "@/components/onboarding/Step3Branding";
import Step4Broadcast from "@/components/onboarding/Step4Broadcast";
import Step5ContentPrefs from "@/components/onboarding/Step5ContentPrefs";
import Step6AIDJ from "@/components/onboarding/Step6AIDJ";
import Step7Social from "@/components/onboarding/Step7Social";
import Step8Credits from "@/components/onboarding/Step8Credits";
import Step9Automation from "@/components/onboarding/Step9Automation";
import Step10FirstContent from "@/components/onboarding/Step10FirstContent";
import Step11Review from "@/components/onboarding/Step11Review";

const TOTAL = 11;

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState(null);
  const [step, setStep] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/state");
      setState(data);
      return data;
    } catch { toast.error("Couldn't load your setup progress"); return null; }
  }, []);

  useEffect(() => {
    (async () => {
      const data = await load();
      const urlStep = Number(params.get("step"));
      if (urlStep >= 1 && urlStep <= TOTAL) setStep(urlStep);
      else setStep(data?.onboarding?.current_step || 1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveStep = async (n, data, status = "complete") => {
    setSaving(true);
    try {
      const res = await api.post(`/onboarding/step/${n}`, { data, status });
      setState((s) => ({ ...s, onboarding: res.data.onboarding, progress_pct: res.data.progress_pct }));
      await load();
      if (n < TOTAL) setStep(n + 1);
      return true;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Couldn't save — try again");
      return false;
    } finally { setSaving(false); }
  };

  const skipAll = async () => {
    try {
      await api.post("/onboarding/skip");
      toast.info("No problem — finish setup anytime from the dashboard checklist");
    } catch { /* noop */ }
    navigate("/dashboard");
  };

  if (!state || step == null) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="font-mono text-xs text-brand-amber" data-testid="onboarding-loading">LOADING SETUP…</div>
      </div>
    );
  }

  const onb = state.onboarding || {};
  const pct = state.progress_pct || 0;
  const common = {
    busy: saving,
    onBack: step > 1 ? () => setStep(step - 1) : undefined,
    station: state.station,
    refresh: load,
  };

  const steps = {
    1: <Step1Welcome firstName={state.first_name} busy={saving}
         onStart={() => saveStep(1, {}, "complete")} onSkipAll={skipAll} />,
    2: <Step2Station {...common} saved={onb.steps?.["2"]} onSave={(d) => saveStep(2, d)} />,
    3: <Step3Branding {...common} saved={onb.steps?.["3"]} onSave={(d) => saveStep(3, d)} onSkip={() => saveStep(3, {}, "skipped")} />,
    4: <Step4Broadcast {...common} saved={onb.steps?.["4"]} broadcast={state.broadcast}
         onSave={(d) => saveStep(4, d)} onSkip={() => saveStep(4, {}, "skipped")} />,
    5: <Step5ContentPrefs {...common} saved={onb.steps?.["5"]} onSave={(d) => saveStep(5, d)} onSkip={() => saveStep(5, {}, "skipped")} />,
    6: <Step6AIDJ {...common} saved={onb.steps?.["6"]} aiDj={state.ai_dj} onSave={(d) => saveStep(6, d)} onSkip={() => saveStep(6, {}, "skipped")} />,
    7: <Step7Social {...common} saved={onb.steps?.["7"]} onSave={(d) => saveStep(7, d)} onSkip={() => saveStep(7, {}, "skipped")} />,
    8: <Step8Credits {...common} saved={onb.steps?.["8"]} onSave={(d) => saveStep(8, d)} onSkip={() => saveStep(8, {}, "skipped")} />,
    9: <Step9Automation {...common} saved={onb.steps?.["9"]} onSave={(d) => saveStep(9, d)} onSkip={() => saveStep(9, {}, "skipped")} />,
    10: <Step10FirstContent {...common} saved={onb.steps?.["10"]} onSave={(d) => saveStep(10, d)} onSkip={() => saveStep(10, {}, "skipped")} />,
    11: <Step11Review {...common} state={state} onboarding={onb} onEditStep={(n) => setStep(n)} />,
  };

  return (
    <div className="min-h-screen bg-bg" data-testid="onboarding-wizard">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-line bg-bg/90 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-brand-amber" />
            <span className="font-display font-bold text-sm text-ink-primary">
              Broadcast<span className="text-brand-amber">Flow</span> AI
            </span>
          </div>
          <button type="button" onClick={() => navigate("/dashboard")} data-testid="onboarding-save-exit"
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary transition">
            <LogOut className="h-3.5 w-3.5" /> Save & finish later
          </button>
        </div>
        {/* Progress */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted" data-testid="onboarding-progress-label">
              Station Setup: {pct}% Complete
            </span>
            <span className="font-mono text-[10px] text-ink-muted">Step {step}/{TOTAL}</span>
          </div>
          <div className="h-1.5 rounded-full bg-bg-elevated overflow-hidden">
            <div className="h-full rounded-full bg-brand-amber transition-all duration-500" style={{ width: `${Math.max(pct, step === 1 ? 2 : 0)}%` }} data-testid="onboarding-progress-bar" />
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {steps[step]}
      </main>
    </div>
  );
}
